




































































































































import {
  computed, defineComponent, ref, watch,
} from '@vue/composition-api';
import type { Ref } from '@vue/composition-api';
import { useRouter } from '@ideavivat/vivat-2';
import { useMutation, useQuery } from '@vue/apollo-composable';

import type { ID } from '@/types';
import useDeliveryAreaCode, { DistanceType } from '@/useDeliveryAreaCode';
import Breadcrumbs from '@/components/Breadcrumbs.vue';
import RoutingForm from '@/components/Setting/AreaCode/RoutingForm.vue';
import VehicleForm from '@/components/Setting/AreaCode/VehicleForm.vue';
import CommonModalWithPreset from '@/components/CommonModalWithPreset.vue';

import createAreaCode from '@/gql/areaCode/createAreaCode.gql';
import updateAreaCode from '@/gql/areaCode/updateAreaCode.gql';
import getHubs from '@/gql/company/getHubs.gql';
import getAreaCode from '@/gql/areaCode/getAreaCode.gql';

interface VehicleValue { pickup: ID; sixWheels: ID; tenWheels: ID; trailer: ID }

const Editor = defineComponent({
  components: {
    Breadcrumbs,
    RoutingForm,
    VehicleForm,
    CommonModalWithPreset,
  },
  setup() {
    const { router, route } = useRouter();
    const { DEFAULT_AREA } = useDeliveryAreaCode();
    const { possibleDistanceTypeDisplay, possibleDistanceType } = useDeliveryAreaCode();
    const getHubsResult = (data: any) => data.me.company.hubs.data;

    const storedDeletedLocationId: Ref<ID[]> = ref([]);
    const backupCode: Ref<string> = ref('');
    const form: Ref<{
      code: string;
      name: string;
      distanceType: DistanceType;
      hub: string;
      expense: VehicleValue;
      travelTime: VehicleValue;
      version: number;
      routes: { id?: ID[]; province: any; districts: any }[];
    }> = ref({
      name: '',
      code: '',
      distanceType: 'NEAR',
      hub: 'MAIN',
      version: 0,
      expense: {
        pickup: 0, sixWheels: 0, tenWheels: 0, trailer: 0,
      },
      travelTime: {
        pickup: 1, sixWheels: 1, tenWheels: 1, trailer: 1,
      },
      routes: [],
    });

    function closeRoutingForm() {
      router.replace({ name: route.value.name, params: route.value.params, query: {} });
    }
    function routingUpdate($event: any) {
      form.value.routes = [...form.value.routes, ...$event].sort((a: any, b: any) => a.province.id - b.province.id);
      closeRoutingForm();
    }
    function pendingEditRoute() {
      router.replace({ name: route.value.name, query: { m: 'ZONE' } });
    }
    function pendingDeleteRoute(index: number) {
      if (form.value.routes[index] && form.value.routes[index].id) {
        storedDeletedLocationId.value.push(...(form.value.routes[index] as any).id);
      }
      form.value.routes.splice(index, 1);
    }

    const computedRoutes = computed(() => {
      const result = [];
      for (let i = 0; i < form.value.routes.length; i += 1) {
        for (let j = 0; j < form.value.routes[i].districts.subdistricts.length; j += 1) {
          result.push({
            province: form.value.routes[i].province?.id,
            district: form.value.routes[i].districts?.id,
            subDistrict: form.value.routes[i].districts.subdistricts[j]?.id,
          });
        }
      }
      return result;
    });

    const computedInput = computed(() => ({
      code: form.value.code,
      name: form.value.name,
      distanceType: form.value.distanceType,
      pickupLeasedCost: form.value.expense.pickup,
      sixWheelLeasedCost: form.value.expense.sixWheels,
      tenWheelLeasedCost: form.value.expense.tenWheels,
      trailerLeasedCost: form.value.expense.trailer,
      pickupTime: form.value.travelTime.pickup,
      sixWheelTime: form.value.travelTime.sixWheels,
      tenWheelTime: form.value.travelTime.tenWheels,
      trailerTime: form.value.travelTime.trailer,
      hub: { connect: (form.value.hub as any).id },
    }));
    const refetchQueries = ['getAreaCodes', 'getAllAreaCodes', 'getAreaCode', 'getProducts', 'getProduct'];
    const createAreaCodeMutation = useMutation(createAreaCode, () => ({
      variables: {
        input: {
          ...computedInput.value,
          location: {
            create: computedRoutes.value.length > 0 ? computedRoutes.value : undefined,
          },
          active: true,
          version: (form.value.version || 0) + 1,
        },
      },
      refetchQueries,
    }));
    const updateAreaCodeMutation = useMutation(updateAreaCode, () => ({
      variables: {
        input: {
          id: route.value.params.id,
          active: false,
          // ...computedInput.value,
          // location: {
          //   upsert: computedRoutes.value.length > 0 ? computedRoutes.value : undefined,
          //   delete: storedDeletedLocationId.value.length > 0 ? storedDeletedLocationId.value : undefined,
          // },
        },
      },
      refetchQueries,
    }));
    updateAreaCodeMutation.onDone(() => {
      createAreaCodeMutation.mutate();
    });
    const computedMutation = computed(() => {
      if (route.value.params.id && route.value.params.id !== -1) return updateAreaCodeMutation;
      return createAreaCodeMutation;
    });
    const doneModal = ref(null);
    createAreaCodeMutation.onDone(() => {
      (doneModal.value as any).compose({ action: 'บันทึก', entity: 'สำเร็จ' });
    });

    const { result: getAreaCodeResult, loading: getAreaCodeLoading } = useQuery(getAreaCode, computed(() => ({
      id: route.value.params.id,
    })), () => ({ enabled: route.value.params.id && route.value.params.id !== -1 }));
    const computedAreaCode = computed(() => getAreaCodeResult.value?.area || { ...DEFAULT_AREA });
    function formatRoutes(apiRoutes: any[]) {
      const res: any[] = [];
      for (let i = 0; i < apiRoutes.length; i += 1) {
        if (
          res.length > 0
          && res[res.length - 1].districts.id === apiRoutes[i].district.id
          && res[res.length - 1].districts.subdistricts.filter((subdist: any) => subdist.zipcode === apiRoutes[i].subDistrict.zipcode)
        ) {
          res[res.length - 1].id.push(apiRoutes[i].id);
          res[res.length - 1].districts.subdistricts.push({ ...apiRoutes[i].subDistrict });
        } else {
          const { province, district, subDistrict } = apiRoutes[i];
          res.push({
            id: [apiRoutes[i].id],
            province,
            districts: { ...district, subdistricts: [{ ...subDistrict }] },
          });
        }
      }
      return res;
    }
    const subdistrictLists = computed(() => {
      const res: any[] = [];
      for (let i = 0; i < form.value.routes.length; i += 1) {
        for (let j = 0; j < form.value.routes[i].districts.subdistricts.length; j += 1) {
          res.push(form.value.routes[i].districts.subdistricts[j].id);
        }
      }
      return res;
    });

    watch(() => computedAreaCode.value, (newVal, oldVal) => {
      if (newVal !== oldVal) {
        form.value.name = computedAreaCode.value.name;
        form.value.code = computedAreaCode.value.code;
        backupCode.value = computedAreaCode.value.code;
        form.value.distanceType = computedAreaCode.value.distanceType;
        form.value.hub = computedAreaCode.value.hub;
        form.value.expense.pickup = computedAreaCode.value.pickupLeasedCost;
        form.value.expense.sixWheels = computedAreaCode.value.sixWheelLeasedCost;
        form.value.expense.tenWheels = computedAreaCode.value.tenWheelLeasedCost;
        form.value.expense.trailer = computedAreaCode.value.trailerLeasedCost;
        form.value.travelTime.pickup = computedAreaCode.value.pickupTime;
        form.value.travelTime.sixWheels = computedAreaCode.value.sixWheelTime;
        form.value.travelTime.tenWheels = computedAreaCode.value.tenWheelTime;
        form.value.travelTime.trailer = computedAreaCode.value.trailerTime;
        form.value.version = computedAreaCode.value.version;
        form.value.routes = formatRoutes(computedAreaCode.value.location);
      }
    });

    return {
      getHubs,
      getHubsResult,
      possibleDistanceTypeDisplay,
      possibleDistanceType,
      form,
      computedRoutes,
      computedMutation,
      closeRoutingForm,
      routingUpdate,
      pendingEditRoute,
      pendingDeleteRoute,
      storedDeletedLocationId,

      doneModal,
      subdistrictLists,
    };
  },
});

export default Editor;
